import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import type {
  ThematicSeed,
  ThematicSeedWithNodes,
  NewThematicSeed,
  UpdateThematicSeed,
} from '@/types/thematicMaps';

export const useThematicSeeds = (mapId: string | undefined) => {
  const [seeds, setSeeds] = useState<ThematicSeedWithNodes[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchSeeds = useCallback(async () => {
    if (!user || !mapId) return;

    try {
      setLoading(true);
      const { data, error } = await (supabase as any)
        .from('thematic_seeds')
        .select(`
          *,
          thematic_nodes(*)
        `)
        .eq('map_id', mapId)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      const seedsWithNodes: ThematicSeedWithNodes[] = (data || []).map((s: any) => ({
        id: s.id,
        user_id: s.user_id,
        map_id: s.map_id,
        name: s.name,
        slug: s.slug,
        intent_family: s.intent_family || [],
        priority: s.priority,
        hub_content: s.hub_content || '',
        hub_title: s.hub_title || '',
        hub_slug_override: s.hub_slug_override || '',
        hub_meta: s.hub_meta || {},
        sort_order: s.sort_order,
        seed_type: s.seed_type || null,
        scope_coverage: s.scope_coverage || [],
        status: s.status || null,
        created_at: s.created_at,
        updated_at: s.updated_at,
        nodes: s.thematic_nodes || [],
        nodes_count: s.thematic_nodes?.length ?? 0,
      }));

      setSeeds(seedsWithNodes);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd pobierania seeds';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [user?.id, mapId, toast]);

  const createSeed = useCallback(async (
    seedData: Omit<NewThematicSeed, 'user_id' | 'map_id'>
  ): Promise<ThematicSeed | null> => {
    if (!user || !mapId) return null;

    try {
      const { data, error } = await (supabase as any)
        .from('thematic_seeds')
        .insert({
          user_id: user.id,
          map_id: mapId,
          ...seedData,
        })
        .select()
        .single();

      if (error) throw error;

      toast({ title: 'Sukces', description: 'Seed został dodany' });
      await fetchSeeds();
      return data as ThematicSeed;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd tworzenia seed';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return null;
    }
  }, [user, mapId, toast, fetchSeeds]);

  const createSeedsBulk = useCallback(async (
    seedsData: Omit<NewThematicSeed, 'user_id' | 'map_id'>[]
  ): Promise<ThematicSeed[]> => {
    if (!user || !mapId) return [];

    try {
      const rows = seedsData.map((s, i) => ({
        user_id: user.id,
        map_id: mapId,
        sort_order: i,
        ...s,
      }));

      // Slug pre-dedup: DB ma unique constraint na (map_id, slug). LLM przy
      // regeneracji moze dac slug ktory juz istnieje — bez dedup leci 409.
      // Suffix -2/-3 przeciw istniejacym seedom w bazie + same-batch.
      const takenSlugs = new Set(seeds.map((s) => s.slug).filter(Boolean));
      let renamedCount = 0;
      const newRows = rows.map((r: any) => {
        if (!r.slug) return r;
        let slug = r.slug;
        if (takenSlugs.has(slug)) {
          let n = 2;
          while (takenSlugs.has(`${r.slug}-${n}`)) n++;
          slug = `${r.slug}-${n}`;
          renamedCount++;
        }
        takenSlugs.add(slug);
        return { ...r, slug };
      });

      console.log('[createSeedsBulk] upserting rows:', newRows.length, 'renamed_slugs:', renamedCount);
      const { data, error } = await (supabase as any)
        .from('thematic_seeds')
        .upsert(newRows, {
          onConflict: 'map_id,slug',
          ignoreDuplicates: true,
        })
        .select();

      if (error) {
        console.error('[createSeedsBulk] Supabase error:', JSON.stringify(error, null, 2));
        throw error;
      }

      const insertedCount = data?.length || 0;
      const skippedCount = newRows.length - insertedCount;
      const parts = [`Dodano ${insertedCount} seeds`];
      if (skippedCount > 0) parts.push(`${skippedCount} duplikatow pominietych`);
      if (renamedCount > 0) parts.push(`${renamedCount} slugow przemianowanych`);
      toast({
        title: 'Sukces',
        description: parts.length > 1 ? `${parts[0]} (${parts.slice(1).join(', ')})` : parts[0],
      });
      await fetchSeeds();
      return (data || []) as ThematicSeed[];
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd dodawania seeds';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return [];
    }
  }, [user, mapId, toast, fetchSeeds, seeds]);

  const updateSeed = useCallback(async (id: string, updates: UpdateThematicSeed): Promise<boolean> => {
    try {
      const { error } = await (supabase as any)
        .from('thematic_seeds')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setSeeds(prev => prev.map(s =>
        s.id === id ? { ...s, ...updates } : s
      ));
      toast({ title: 'Sukces', description: 'Seed zaktualizowany' });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd aktualizacji seed';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return false;
    }
  }, [toast]);

  const deleteSeed = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await (supabase as any)
        .from('thematic_seeds')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setSeeds(prev => prev.filter(s => s.id !== id));
      toast({ title: 'Sukces', description: 'Seed usunięty' });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd usuwania seed';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return false;
    }
  }, [toast]);

  useEffect(() => {
    if (user?.id && mapId) {
      fetchSeeds();
    }
  }, [user?.id, mapId, fetchSeeds]);

  const { isLive } = useRealtimeSubscription({
    channel: `thematic_seeds_${mapId || 'none'}`,
    table: 'thematic_seeds',
    filter: mapId ? `map_id=eq.${mapId}` : undefined,
    enabled: Boolean(user?.id && mapId),
    onChange: fetchSeeds,
  });

  return {
    seeds,
    loading,
    isLive,
    createSeed,
    createSeedsBulk,
    updateSeed,
    deleteSeed,
    refreshSeeds: fetchSeeds,
  };
};
